import { useTranslation } from 'react-i18next';
import { Typography } from 'app/components/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LabeledSection } from './LabeledSection';
import { DimensionsSection } from './DimensionsSection';
import { CalculationsSection } from './CalculationsSection';
import { NotesSection } from './NotesSection';
import { TagContentBlocks } from './TagContentBlocks';
import { buildChainContaining } from '../Footer/tagChainUtils';
import { Analysis } from './Analysis';
import { StatusField } from './StatusField';
import { ScaleField } from './ScaleField';
import { AccuracyField } from './AccuracyField';
import { CurrencyField } from './CurrencyField';
import { DateField } from './DateField';
import { FormatField } from './FormatField';
import {
  TagWithMetadata,
  useCalculations,
  useDeleteTag,
  useDocumentTaxonomyConfig,
  useExtensions,
  usePresentationTree,
  CalculationGroupResponse,
  useCreateCalculationRule,
  useUpdateCalculationRule,
} from 'entities/tag';
import { useSlateStatic } from 'slate-react';
import { NoDefaultWarning } from './NoDefaultWarning';
import { getMissingDefaults, hasMissingDefaults } from 'features/tagging/Taxonomies/missingDefaults';
import { resolveConceptLabelOrName, getConceptTypeContent } from 'app/utils/tag-utils';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { useCalcPick } from 'app/state/contexts/CalcPickContext';
import { SignSwitch } from './SignSwitch';
import { NilSwitch } from './NilSwitch';
import { EscapeSwitch } from './EscapeSwitch';
import { HiddenSwitch } from './HiddenSwitch';
import { HiddenValueField } from './HiddenValueField';
import { FractionValueFields } from './FractionValueFields';
import { FootnotesSection } from './FootnotesSection';
import { buildNegatedPresentationIndex, computeSignHint } from './signNegatedHint';
import { buildCalcPickDraftFromGroup, draftToSummands, computeTermHighlightIds } from './calcPickFlow';
import { useTaggedChainValues } from './useTaggedChainValues';
import { convertTagToExclude, withoutUndo } from 'slate/extensions/tag';
import { TagInfoFooter } from '../Footer/TagInfoFooter';
import { ConfirmationModal } from 'app/components';
import { useGoToTag } from 'app/utils/hooks/useGoToTag';

import './TagInfo.scss';
import { useEditorReferences } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';

interface TagInfoProps {
  selectedConceptName: string | null;
  selectedTag: TagWithMetadata | null;
  onDeleteClick?: (tag: TagWithMetadata) => void;
  onDeletePartClick?: (tag: TagWithMetadata) => void;
  onDeleteChainClick?: (tags: TagWithMetadata[]) => void;
  onSelectTag?: (tag: TagWithMetadata | null) => void;
  isContentMarked: boolean;
  documentTags?: TagWithMetadata[];
  readOnly?: boolean;
  onCancel?: () => void;
}

export const TagInfo = ({
  selectedConceptName,
  selectedTag: activeTag,
  onDeleteClick,
  onDeletePartClick,
  onDeleteChainClick,
  onSelectTag,
  isContentMarked,
  documentTags,
  readOnly = false,
  onCancel,
}: TagInfoProps) => {
  const { t } = useTranslation('tag');

  const { data: pDocument } = useEditorDocument();
  const references = useEditorReferences();
  const editor = useSlateStatic();
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const { tagLanguage, setTermHighlightTagIds } = useTagDisplay();
  const { calcPickMode, enterPick, undo, canUndo, cancel } = useCalcPick();
  const updateRule = useUpdateCalculationRule();
  const createRule = useCreateCalculationRule();
  const { mutate: deleteTagMutation } = useDeleteTag();
  const { data: presentationTree } = usePresentationTree(pDocument?.superId);
  const { data: extensions } = useExtensions(pDocument?.superId, activeTag?.taxonomyId);
  const negatedIndex = useMemo(() => buildNegatedPresentationIndex(presentationTree ?? null), [presentationTree]);

  const taggedValue = useTaggedChainValues(editor, activeTag, documentTags, references);
  const { goToTag, getGoToTagState } = useGoToTag();
  const [sectionSwitchTag, setSectionSwitchTag] = useState<TagWithMetadata | null>(null);

  const selectChainPart = useCallback(
    (tagId: string) => {
      const tag = documentTags?.find((t) => t.id === tagId);
      if (!tag) return;
      const { kind } = getGoToTagState(tag);
      if (kind === 'blocked') return;
      if (kind === 'navigate') {
        setSectionSwitchTag(tag);
        return;
      }
      onSelectTag?.(tag);
      if (kind === 'scroll') goToTag(tag);
    },
    [documentTags, getGoToTagState, goToTag, onSelectTag]
  );

  const canSelectChainPart = useCallback(
    (tagId: string) => {
      const tag = documentTags?.find((t) => t.id === tagId);
      return !!tag && getGoToTagState(tag).kind !== 'blocked';
    },
    [documentTags, getGoToTagState]
  );

  const deleteChainPart = useCallback(
    (tagId: string) => {
      const tag = documentTags?.find((t) => t.id === tagId);
      if (tag) onDeletePartClick?.(tag);
    },
    [documentTags, onDeletePartClick]
  );

  const chainTags = useMemo(() => (activeTag ? buildChainContaining(documentTags ?? [], activeTag.id) : []), [activeTag, documentTags]);

  const conceptName = activeTag?.concept?.name || selectedConceptName;
  const {
    data: calculationGroups,
    isLoading: calculationsLoading,
    isFetching: calculationsFetching,
    isPlaceholderData: calculationsIsPreviousData,
  } = useCalculations(pDocument?.superId, conceptName, activeTag?.contextId, tagLanguage, activeTag?.id);

  useEffect(() => {
    setTermHighlightTagIds(computeTermHighlightIds(calculationGroups, activeTag?.conceptId));
    return () => setTermHighlightTagIds(new Set());
  }, [activeTag, calculationGroups, setTermHighlightTagIds]);

  useEffect(() => {
    if (!calcPickMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [calcPickMode, cancel]);

  const enterPickMode = useCallback(
    (group: CalculationGroupResponse | null) => {
      if (!activeTag) return;
      enterPick(buildCalcPickDraftFromGroup(group, { conceptId: activeTag.conceptId, conceptName: conceptName ?? '', tagId: activeTag.id }));
    },
    [activeTag, conceptName, enterPick]
  );

  const savePickMode = useCallback(() => {
    if (!calcPickMode || !activeTag || !pDocument?.superId) return;
    const summands = draftToSummands(calcPickMode);
    if (summands.length === 0) {
      cancel();
      return;
    }
    if (calcPickMode.ruleId) {
      updateRule.mutate({ ruleId: calcPickMode.ruleId, documentSuperId: pDocument.superId, summands });
    } else {
      createRule.mutate({ documentSuperId: pDocument.superId, parentConceptId: activeTag.conceptId, linkroleUri: null, summands });
    }
    cancel();
  }, [calcPickMode, activeTag, pDocument, updateRule, createRule, cancel]);

  if (!activeTag && selectedConceptName) {
    return (
      <div className="p-3 w-100">
        <Typography>{t('info.only.taxonomy')}</Typography>
      </div>
    );
  }

  if (!activeTag) {
    return (
      <div className="p-3 w-100">
        <Typography>{t('no.valid.tag')}</Typography>
      </div>
    );
  }

  const label = resolveConceptLabelOrName({ concept: activeTag.concept, currentLang: tagLanguage, fallback: '' });
  const conceptType = activeTag.concept?.conceptType || null;
  const { conceptLabel } = conceptType ? getConceptTypeContent(conceptType) : { conceptLabel: t('concept.default') };
  const documentation = activeTag.concept?.labels?.[tagLanguage]?.documentation || '';

  const preferredAccuracyType: 'decimals' | 'precision' = (config?.preferredFormat as 'decimals' | 'precision') ?? 'decimals';
  const tagCurrency = activeTag.unit?.measure?.replace(/^iso4217:/, '') ?? null;
  const configActiveCurrency = config?.currencySelections?.find((cur) => cur.isActive)?.currency ?? null;
  const documentSuperId = pDocument?.superId;

  const hasIncompleteConfig = config !== undefined && hasMissingDefaults(getMissingDefaults(config));

  const hiddenSnapshot = taggedValue.find((block) => block.tagId === activeTag.id)?.value ?? activeTag.metadata?.textSnippet ?? '';
  const activeConceptName = activeTag.concept?.name ?? '';
  const isExtensionConcept = (extensions ?? []).some((e) => e.id === activeTag.conceptId || e.name === activeConceptName.replace(/^[^:]*:/, ''));
  const signHint = computeSignHint({
    sign: activeTag.sign ?? null,
    conceptKeys: [activeConceptName, activeConceptName.replace(/^[^:]*:/, '')],
    negatedIndex,
    isExtensionConcept,
  });

  return (
    <>
      <section className="tag-info p-3 w-100">
        <div className="flex-row">
          <span />
          <StatusField tagId={activeTag.id} documentSuperId={documentSuperId} currentStatus={activeTag.status ?? 'draft'} disabled={readOnly} />
        </div>

        <LabeledSection label={conceptLabel}>
          <div className="flex-column">
            <Typography variant="body-8">{label}</Typography>
            <Typography variant="body-3" fontWeight="light" color="#000000" style={{ wordBreak: 'break-all' }}>
              {activeTag.concept?.name || selectedConceptName || ''}
            </Typography>
          </div>
        </LabeledSection>

        <LabeledSection label={t('documentation.title')}>
          <Typography variant="body-7" fontWeight="light" style={{ wordBreak: 'break-all' }}>
            {documentation || t('documentation.empty')}
          </Typography>
        </LabeledSection>

        <NotesSection key={activeTag.id} defaultValue={activeTag.notes ?? ''} tagId={activeTag.id} documentSuperId={documentSuperId} disabled={readOnly} />

        <TagContentBlocks
          taggedValue={taggedValue}
          activeTagId={activeTag.id}
          onSelectBlock={selectChainPart}
          canSelectBlock={canSelectChainPart}
          onDeleteBlock={readOnly || !onDeletePartClick ? undefined : deleteChainPart}
          onDeleteAll={readOnly || !onDeleteChainClick || chainTags.length < 2 ? undefined : () => onDeleteChainClick(chainTags)}
        />
        <ConfirmationModal
          centered
          show={!!sectionSwitchTag}
          message={t('content.switch.section', { section: sectionSwitchTag?.metadata?.sectionName ?? '' })}
          onDecline={() => setSectionSwitchTag(null)}
          onConfirm={() => {
            if (sectionSwitchTag) goToTag(sectionSwitchTag);
            setSectionSwitchTag(null);
          }}
        />

        <DimensionsSection tag={activeTag} documentSuperId={documentSuperId} readOnly={readOnly} />

        <FootnotesSection tagId={activeTag.id} documentSuperId={documentSuperId} disabled={readOnly} />

        <LabeledSection label={t('date.label')}>
          <DateField
            tagId={activeTag.id}
            documentSuperId={documentSuperId}
            selectedContextId={activeTag.contextId}
            tagContext={activeTag.context ?? null}
            contextSelections={config?.contextSelections ?? []}
            configId={config?.id}
            disabled={readOnly}
          />
        </LabeledSection>

        <LabeledSection label={t('properties.title')}>
          {hasIncompleteConfig && <NoDefaultWarning documentSuperId={documentSuperId} />}

          {activeTag.type === 'nonFraction' && (
            <>
              <CurrencyField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                selectedCurrency={tagCurrency ?? configActiveCurrency}
                currencySelections={config?.currencySelections ?? []}
                unit={activeTag.unit ?? null}
                configId={config?.id}
                disabled={readOnly}
              />

              <FormatField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                currentFormat={activeTag.format ?? config?.format ?? null}
                disabled={readOnly}
              />

              <ScaleField tagId={activeTag.id} documentSuperId={documentSuperId} currentScale={activeTag.scale ?? config?.scale ?? null} disabled={readOnly} />

              <AccuracyField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                tagDecimals={activeTag.decimals ?? null}
                tagPrecision={activeTag.precision ?? null}
                configDecimals={config?.decimals ?? null}
                configPrecision={config?.precision ?? null}
                preferredType={preferredAccuracyType}
                disabled={readOnly}
              />

              <SignSwitch tagId={activeTag.id} documentSuperId={documentSuperId} currentSignValue={activeTag.sign ?? null} disabled={readOnly} />

              {signHint && (
                <Typography variant="body-1" style={{ color: '#7a5c1e' }}>
                  {t('sign.hint.negated-presentation')}
                </Typography>
              )}
            </>
          )}

          {activeTag.type === 'fraction' && (
            <CurrencyField
              tagId={activeTag.id}
              documentSuperId={documentSuperId}
              selectedCurrency={tagCurrency ?? configActiveCurrency}
              currencySelections={config?.currencySelections ?? []}
              unit={activeTag.unit ?? null}
              configId={config?.id}
              disabled={readOnly}
            />
          )}

          <div className="property-toggles">
            <NilSwitch tagId={activeTag.id} documentSuperId={documentSuperId} currentNilValue={activeTag.nil ?? false} disabled={readOnly} />

            {activeTag.type === 'nonNumeric' && (
              <EscapeSwitch
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                currentEscapeValue={activeTag.escape ?? null}
                conceptType={conceptType}
                disabled={readOnly || (activeTag.nil ?? false)}
              />
            )}

            <HiddenSwitch
              tagId={activeTag.id}
              documentSuperId={documentSuperId}
              currentHiddenValue={activeTag.hidden ?? false}
              snapshotValue={hiddenSnapshot}
              isPlaced={!!activeTag.onId}
              disabled={readOnly}
            />
          </div>

          {activeTag.hidden &&
            (activeTag.type === 'fraction' ? (
              <FractionValueFields
                key={`fraction-value-${activeTag.id}`}
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                defaultValue={activeTag.value ?? ''}
                disabled={readOnly}
              />
            ) : (
              <HiddenValueField
                key={`hidden-value-${activeTag.id}`}
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                defaultValue={activeTag.value ?? ''}
                disabled={readOnly}
              />
            ))}
        </LabeledSection>
        {activeTag.type === 'nonFraction' && (
          <>
            {onSelectTag && <Analysis activeTag={activeTag} documentSuperId={documentSuperId} onSelectTag={onSelectTag} documentTags={documentTags ?? []} />}
            {calcPickMode && (
              <div
                className="flex flex-row justify-between"
                style={{ alignItems: 'center', gap: 8, padding: '4px 6px', backgroundColor: '#fdebc8', borderRadius: 4 }}
              >
                <Typography variant="body-1" style={{ color: '#7a5c1e', minWidth: 0 }}>
                  {t('calculations.pick-mode-hint')}
                </Typography>
                <span className="flex flex-row" style={{ gap: 10, flexShrink: 0 }}>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#7a5c1e' }}
                    onClick={savePickMode}
                  >
                    {t('calculations.pick-done')}
                  </button>
                  {canUndo && (
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#7a5c1e' }} onClick={undo}>
                      {t('calculations.pick-undo')}
                    </button>
                  )}
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#7a5c1e' }} onClick={cancel}>
                    {t('calculations.pick-cancel')}
                  </button>
                </span>
              </div>
            )}
            <CalculationsSection
              calculationGroups={calculationGroups}
              isLoading={calculationsLoading}
              isFetching={calculationsFetching}
              isPreviousData={calculationsIsPreviousData}
              authoring={
                !readOnly && documentSuperId && conceptName
                  ? {
                      documentSuperId,
                      parentConceptId: activeTag.conceptId,
                      selectedConceptName: conceptName,
                      documentTags: documentTags ?? [],
                      onPickTerms: enterPickMode,
                      onCreateSum: () => enterPickMode(null),
                    }
                  : undefined
              }
            />
          </>
        )}

        {!readOnly && !activeTag.hidden && !activeTag.metadata?.tableName && !activeTag.metadata?.blockCarrier && (
          <button
            className="btn btn-secondary w-100 mb-2"
            onClick={() => {
              let converted = false;
              withoutUndo(editor, () => {
                converted = convertTagToExclude(editor, activeTag.id);
              });
              if (converted) {
                deleteTagMutation({ id: activeTag.id, documentSuperId, skipSectionRewrite: true });
                onCancel?.();
              }
            }}
          >
            {t('exclude.convert')}
          </button>
        )}

        {onDeleteClick && (
          <button className="tag-info__delete-btn" onClick={() => onDeleteClick(activeTag)}>
            <i className="fa-regular fa-trash" />
            <Typography variant="body-7" fontWeight="semibold" color="#fff">
              {t('delete.button')}
            </Typography>
          </button>
        )}
      </section>

      <TagInfoFooter selectedTag={activeTag} isContentMarked={isContentMarked} documentTags={documentTags ?? []} onCancel={onCancel} />
    </>
  );
};
